import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { Overview } from 'components/Overview'
import { AccountType, userHasAccountType } from 'config'
import { pendingChangeRequestTypes } from 'pages/LoanStructure/constant'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { cancelManuallyChangeRequest, getLoanChangeRequest, postLoanChangeRequest } from 'services'
import { Button, Modal, TextArea } from 'stories/components'
import { confirm, formatTime } from 'utils'
import { setLoanNumber } from 'utils/setLoanNumber'

export default function LoanChangeRequest({ child = false }: { child?: boolean }) {
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [changeDetail, setChangeDetail] = useState('')
  const [action, setAction] = useState('')
  const [data, setData] = useState<Array<Record<string, any>>>([])

  const { auth } = useSelector((state: any) => {
    return {
      auth: state.auth,
    }
  })

  const initData = () => {
    setLoading(true)
    setLoanNumber()
    getLoanChangeRequest().then(({ data = [] }) => {
      const _data = data.reverse()
      setData(_data)
      setLoading(false)
    })
  }

  useEffect(() => {
    initData()
  }, [])

  const addRequest = () => {
    setChangeDetail('')
    setIsOpen(true)
  }
  const onConfirmRequest = async () => {
    if (changeDetail.length === 0) {
      return toast('Loan Change Detail is Required!', { type: 'error' })
    }
    setAction('onConfirmRequest')
    const res = await postLoanChangeRequest({ detail: changeDetail })
    let _data = cloneDeep(data)
    _data = [res.data, ..._data]
    setData(_data)
    setAction('')
    setIsOpen(false)
    return true
  }

  const cancelRequest = async (taskID: number) => {
    const content = (
      <div className="text-gray-400 mb-4 text-[17px]">Are you sure want to cancel this loan change request?</div>
    )
    const result = await confirm(content)
    if (!result) return
    setLoading(true)
    await cancelManuallyChangeRequest(taskID)
    initData()
  }

  const renderByWithStatus = (item: any) => {
    try {
      if (userHasAccountType(auth.profile, AccountType.ADMIN) && item.status === 1) {
        if ([1, 2, 3].includes(item.type)) {
          return (
            <span className="statusColor">
              <div className="">
                <div className="my-3">{item.by}</div>
                <Button link onClick={() => cancelRequest(item.taskID)}>
                  <span className="text-[14px]">Cancel Request</span>
                </Button>
              </div>
            </span>
          )
        }
      }
      if (item.status === 2) {
        return (
          <span className="statusColor">
            <div className="">
              <div className="my-3">{item.by}</div>
              <div className="p-1 rounded complete">
                Completed
                <br />
                {item.completedBy}
                <br />
                {formatTime(item.completedDate)}
              </div>
            </div>
          </span>
        )
      }
      if (item.status === 3) {
        return (
          <span className="statusColor">
            <div className="">
              <div className="my-3">{item.by}</div>
              <div className="p-1 rounded Cancelled">
                Cancelled
                <br />
                {item.completedBy}
                <br />
                {formatTime(item.completedDate)}
              </div>
            </div>
          </span>
        )
      }
      if (item.status === 4) {
        return (
          <span className="statusColor">
            <div className="">
              <div className="my-3">{item.by}</div>
              <div className="p-1 rounded Cancelled">
                Declined
                <br />
                {item.completedBy}
                <br />
                {formatTime(item.completedDate)}
              </div>
            </div>
          </span>
        )
      }
    } catch {}
    return item.by
  }

  return (
    <div className={`LoanChangeRequest-container ${!child ? 'px-1 py-4 sm:px-2 sm:py-6' : ''}`}>
      {!child && <Overview title="Loan Change Request" />}
      <div className="max-w-screen-2xl m-auto">
        <div className={`relative bg-white ${child ? '' : 'mb-4 rounded p-2 shadow1 sm:p-4'}`}>
          <LayoutLoading show={loading} />
          <div className="my-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
            <div className="text-[18px] font-variation-settings-600 sm:ml-2">Requests</div>
            <div className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto" onClick={addRequest}>
                Add Request
              </Button>
            </div>
          </div>
          <div className="parties-container mb-6 overflow-auto shadow-md sm:rounded-lg">
            <table className="w-full min-w-[800px] pl-6 text-left text-sm text-gray-900 dark:text-gray-400">
              <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th scope="col" className="px-6 py-3">
                    Task No
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Assigned To
                  </th>
                  <th scope="col" className="px-6 py-3">
                    <div className="w-fit border-b border-b-gray-300 mb-2">Description</div>
                    Note
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Requested by
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Requested Date
                  </th>
                </tr>
              </thead>
              <tbody className="text-[14px] text-gray-900">
                {data.map((item, index) => {
                  return (
                    <tr key={index} className={`${index % 2 ? 'bg-gray-50' : ''}`}>
                      <td className="px-6 py-3">{item.taskNo}</td>
                      <td className="px-6 py-3">
                        {item.type === 1 ? 'All Lock Desk' : 'Admin, Lock Desk, UW Manager'}
                      </td>
                      <td className="px-6 py-3">
                        <div className="border-b border-b-gray-300 mb-2 font-semibold w-fit">
                          {pendingChangeRequestTypes[item.type]}
                        </div>
                        <span dangerouslySetInnerHTML={{ __html: item.detail }} />
                      </td>
                      <td className="px-6 py-3">{renderByWithStatus(item)}</td>
                      <td className="px-6 py-3">{formatTime(item.time)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Modal
        button={<span></span>}
        title={`Loan Change Request`}
        titleOkay="Confirm"
        titleCancel="Close"
        isOpen={isOpen}
        lastUpdatedAt={Date.now()}
        loading={action === 'onConfirmRequest'}
        onOk={onConfirmRequest}
        onClose={() => setIsOpen(false)}
      >
        <div className="w-[calc(100vw-5rem)] max-w-[560px]">
          <TextArea
            title={'Change Request Detail'}
            key={'detail'}
            rows={6}
            value={changeDetail}
            required={true}
            onChange={(value) => setChangeDetail(value)}
          />
        </div>
      </Modal>
    </div>
  )
}
